import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { platform } from "../platform/index.js";

/**
 * **نافذةٌ عائمة فوق كل شيء — موضعٌ واحد**: بطاقةُ الشخص والتقويمُ وقائمةُ الإشارة.
 *
 * تُرسم عند جذر الصفحة، فلا يقصّها درجٌ ولا نافذةٌ لها حدود. وعلى الهاتف تصير لوحاً من الأسفل
 * بالعرض كلّه (من الأنماط)، فلا تخرج عن الشاشة.
 *
 * **والإغلاقُ بالضغط خارجها وبـEscape ليس هنا:** حارسٌ واحدٌ في المنصّة يقرأ أعلى نافذة.
 */
export function Floating({
  anchor,
  label,
  role = "dialog",
  id,
  className,
  children,
  onPointerEnter,
  onPointerLeave,
}: {
  readonly anchor: HTMLElement;
  readonly label: string;
  readonly role?: "dialog" | "listbox";
  readonly id?: string;
  readonly className?: string;
  readonly children: ReactNode;
  readonly onPointerEnter?: () => void;
  readonly onPointerLeave?: () => void;
}): ReactNode {
  const box = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ readonly top: number; readonly left: number } | null>(null);

  useLayoutEffect(() => {
    const measure = (): void => {
      const panel = box.current;
      if (panel === null) return;
      const at = anchor.getBoundingClientRect();
      const { width, height } = panel.getBoundingClientRect();
      const screen = platform.viewport();
      const gap = 6;
      const below = at.bottom + gap;
      const top =
        below + height > screen.height - gap ? Math.max(gap, at.top - height - gap) : below;
      /** **ويبدأ من حافة صاحبه في العربية** — يمينُه على يمينه، ولا يخرج عن الشاشة */
      const left = Math.min(Math.max(gap, at.right - width), screen.width - width - gap);
      setPlace({ top, left: Math.max(gap, left) });
    };
    measure();
    return platform.onViewportChange(measure);
  }, [anchor]);

  const root = platform.overlayRoot;
  const panel = (
    <div
      ref={box}
      id={id}
      role={role}
      aria-label={label}
      data-floating
      className={className === undefined ? "floating" : `floating ${className}`}
      style={place === null ? { visibility: "hidden" } : { top: place.top, left: place.left }}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      {children}
    </div>
  );
  return root === null ? panel : createPortal(panel, root);
}
